import { useState } from 'react';
import { SEOHeadTags, useSEO } from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials } from '@/mocks/videoData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const seo = useSEO({
    title: '关于我们 - 国产视频在线观看',
    description: '国产视频在线观看平台品牌介绍、用户口碑与联系方式。了解我们的服务理念，海量高清国产影视剧、综艺、纪录片免费在线播放。',
    keywords: '国产视频在线观看,关于我们,品牌介绍,用户口碑,联系我们,国产视频平台',
    canonicalUrl: 'https://guochan.video/about',
  });

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '国产视频在线观看',
    url: 'https://guochan.video/',
    description: '国产视频在线观看平台，提供海量高清国产影视剧、综艺、纪录片免费在线播放',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qqovvnahb7382ovdrg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHeadTags data={seo} />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="pt-24 pb-6 bg-background-100">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4">
                关于我们
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                了解国产视频在线观看平台
              </p>
            </div>
          </div>

          <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                  品牌故事
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4 mb-6">
                  让每一个中国人都能免费看好视频
                </h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed">
                  <p>
                    国产视频在线观看平台成立于2020年，是一家专注于国产影视内容传播的在线视频平台。
                    我们致力于为全国用户提供海量高清国产影视剧、综艺节目、纪录片免费在线播放服务。
                  </p>
                  <p>
                    从最初的数百部影片到今天超过10万+的视频资源库，我们始终坚持"免费、高清、便捷"的服务理念。
                    无需下载APP，无需注册会员，打开浏览器就能流畅观看您喜爱的国产视频内容。
                  </p>
                  <p>
                    我们的内容团队每天都在精心筛选和更新最新的国产视频资源。从热播电视剧到院线大片，
                    从热门综艺到精品纪录片，从国产动漫到少儿节目——六大内容板块全面覆盖，满足不同年龄段、不同兴趣偏好用户的观看需求。
                  </p>
                  <p>
                    我们深知，国产视频承载着中国文化的精神内核。通过这个平台，我们希望让更多人便捷地接触到优秀的国产影视作品，
                    感受中国故事的独特魅力。无论是讲述时代变迁的现实主义力作，还是展现中国美学的古装精品，
                    亦或是激发民族自豪感的纪录片——每一部作品都是中国文化的一部分，都值得被更多人看见。
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-6 mt-10">
                  <div className="text-center p-5 rounded-xl bg-accent-100/80">
                    <div className="text-2xl md:text-3xl font-bold text-accent-700">10万+</div>
                    <div className="text-xs text-accent-600 mt-1">视频资源</div>
                  </div>
                  <div className="text-center p-5 rounded-xl bg-primary-100/80">
                    <div className="text-2xl md:text-3xl font-bold text-primary-700">5000万+</div>
                    <div className="text-xs text-primary-600 mt-1">月活用户</div>
                  </div>
                  <div className="text-center p-5 rounded-xl bg-secondary-100/80">
                    <div className="text-2xl md:text-3xl font-bold text-secondary-700">6大</div>
                    <div className="text-xs text-secondary-600 mt-1">内容分类</div>
                  </div>
                </div>
              </div>

              <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20office%20with%20large%20screen%20displaying%20colorful%20media%20content%2C%20creative%20workspace%2C%20warm%20lighting%2C%20professional%20atmosphere%2C%20clean%20aesthetic%2C%20Chinese%20tech%20company%20vibe&width=800&height=600&seq=about-brand-image&orientation=landscape"
                  alt="国产视频在线观看 - 品牌形象"
                  title="国产视频在线观看平台"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                  用户口碑
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4">
                  用户对我们的评价
                </h2>
                <p className="text-sm text-foreground-500 mt-2 max-w-[500px] mx-auto">
                  来自真实用户的使用反馈，您的满意是我们前进的动力
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((t, idx) => (
                  <div
                    key={idx}
                    className="p-6 rounded-xl bg-background-50 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(t.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      &ldquo;{t.comment}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-sm font-bold text-primary-600">{t.avatar}</span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                        <div className="text-xs text-foreground-400">{t.date}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-[640px] mx-auto">
              <div className="text-center mb-10">
                <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4">
                  有任何问题？随时联系我们
                </h2>
                <p className="text-sm text-foreground-500 mt-2">
                  您的反馈和建议对我们至关重要，我们会在第一时间回复
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的电子邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入主题"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <div className="honeypot-wrap">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3.5 bg-primary-500 text-background-50 rounded-lg font-semibold text-base hover:bg-primary-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </span>
                  ) : (
                    '发送留言'
                  )}
                </button>

                {formStatus === 'success' && (
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm text-center">
                    <i className="ri-checkbox-circle-line mr-1"></i>
                    感谢您的留言！我们会尽快与您联系。
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm text-center">
                    <i className="ri-error-warning-line mr-1"></i>
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}